// The great prestige monument: built as a plain stone plinth, then it grows
// through material tiers (wooden effigy -> stone statue -> metal monument ->
// golden colossus -> jewelled wonder) as goods are donated. Ported 1:1 from
// age-of-mcp's monument.ts: the shared foundation/scaffold come from the
// library, the plinth and tiered statue are local helpers, and the four build
// stages are transcribed from stage(s) (default tier 0).
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, type Part } from "../../lib";

const STONE = "#8a8f96";
const STONE_DK = "#6c7177";
const WOOD = "#9c6b3f";
const METAL = "#9fb3c2";
const GOLD = "#e8c25a";
const GEM = "#69e0d0";
const TAU = Math.PI * 2;
const BASE = 0.14;

// Per-tier crown: [column colour, column height, accent].
const TIERS: { color: string; h: number; accent?: string }[] = [
  { color: STONE_DK, h: 0.6 }, // 0 bare plinth — a stub
  { color: WOOD, h: 1.3 }, // 1 wooden effigy
  { color: STONE, h: 1.9 }, // 2 stone statue
  { color: METAL, h: 2.5, accent: METAL }, // 3 metal monument
  { color: GOLD, h: 3.1, accent: GOLD }, // 4 golden colossus
  { color: GOLD, h: 3.7, accent: GEM }, // 5 jewelled wonder
];

// A stepped stone plinth filling the 3x3 plot (footprint-native).
function plinth(): Part[] {
  return [
    foundationSlab(2.9),
    { geometry: new THREE.BoxGeometry(2.0, 0.34, 2.0), color: STONE, position: [0, BASE + 0.17, 0] },
    { geometry: new THREE.BoxGeometry(1.4, 0.30, 1.4), color: STONE_DK, position: [0, BASE + 0.34 + 0.15, 0] },
  ];
}
const PLINTH_TOP = BASE + 0.34 + 0.30;

// The tiered column + crown that rises from the plinth.
function statue(tier: number): Part[] {
  const t = TIERS[Math.max(0, Math.min(TIERS.length - 1, tier))]!;
  const parts: Part[] = [];
  const colR = 0.34;
  // A faceted tapering column.
  parts.push({
    geometry: new THREE.CylinderGeometry(colR * 0.7, colR, t.h, 6),
    color: t.color, position: [0, PLINTH_TOP + t.h / 2, 0],
  });
  const top = PLINTH_TOP + t.h;
  // A crowning element grows richer with the tier.
  if (tier <= 0) {
    parts.push({ geometry: new THREE.BoxGeometry(0.5, 0.18, 0.5), color: STONE, position: [0, top + 0.09, 0] });
  } else if (tier <= 2) {
    // A simple figure: body + head.
    parts.push({ geometry: new THREE.BoxGeometry(0.5, 0.6, 0.32), color: t.color, position: [0, top + 0.3, 0] });
    parts.push({ geometry: new THREE.SphereGeometry(0.22, 8, 6), color: t.color, position: [0, top + 0.74, 0] });
  } else {
    // A colossus: body, head, raised arms, and a crowning accent.
    const acc = t.accent ?? GOLD;
    parts.push({ geometry: new THREE.BoxGeometry(0.62, 0.8, 0.4), color: t.color, position: [0, top + 0.4, 0] });
    parts.push({ geometry: new THREE.SphereGeometry(0.26, 10, 8), color: t.color, position: [0, top + 0.98, 0] });
    // Raised arms.
    for (const sx of [-1, 1]) {
      parts.push({
        geometry: new THREE.CylinderGeometry(0.1, 0.1, 0.7, 6), color: t.color,
        position: [sx * 0.42, top + 0.62, 0], rotation: [0, 0, sx * 0.9],
      });
    }
    // A crowning orb / gem held aloft.
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.24, 0), color: acc, position: [0, top + 1.4, 0] });
    // Jewel accents ringing the base of the colossus (top tier).
    if (tier >= 5) {
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * TAU;
        parts.push({
          geometry: new THREE.IcosahedronGeometry(0.12, 0), color: GEM,
          position: [Math.cos(a) * 0.8, PLINTH_TOP + 0.12, Math.sin(a) * 0.8],
        });
      }
    }
  }
  return parts;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return plinth().slice(0, 2); // foundation + first step
  if (s === 1) return [...plinth(), ...scaffold(1.6, 0.8)];
  if (s === 2) return [...plinth(), ...statue(0).slice(0, 1), ...scaffold(1.2, 1.6)];
  // Finished — the column + crown grown to the current material tier (default 0).
  return [...plinth(), ...statue(0)];
}

export const monument = defineModel({
  id: "monument",
  tiles: { w: 3, h: 3 },
  batchId: "standard",
  name: "Monument",
  tags: ["building", "monument", "low-poly", "age-of-mcp", "classic"],
  summary: "Low-poly monument.",
  description: "A colossal landmark built as a plain plinth and enriched with refined goods — a grand prestige piece.",
  author: "age-of-mcp",
  footprint: 1.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
